<template>
  <div class="modal show">
    <div class="modal-content">
      <span class="close" @click="$emit('close')">&times;</span>
      <h2>{{ t('vm.resizeVM.title') }}</h2>

             <div class="current-specs">
         <h3>{{ t('vm.resizeVM.currentSpecs') }}</h3>
         <div class="spec-item">
           <span class="spec-label">{{ t('vm.machineType') }}:</span>
           <span class="spec-value">{{ getCurrentMachineType() }}</span>
         </div>
         <div v-if="vm.guestAccelerators?.length" class="spec-item">
           <span class="spec-label">{{ t('vm.gpu') }}:</span>
           <span class="spec-value">{{ getCurrentGPUInfo() }}</span>
         </div>
         <div class="spec-item">
           <span class="spec-label">{{ t('createVM.serviceAccount') }}:</span>
           <span class="spec-value">{{ getCurrentServiceAccount() }}</span>
         </div>
       </div>

      <form @submit.prevent="handleSubmit">
        <!-- GPU选择 -->
        <div class="form-group">
          <label for="gpuType">{{ t('createVM.gpuType') }}:</label>
          <select id="gpuType" v-model="formData.gpuType" @change="updateAvailableMachineTypes">
            <option value="">{{ t('createVM.gpuTypes.none') }}</option>
            <option value="nvidia-l4">NVIDIA L4</option>
            <option value="nvidia-tesla-t4">NVIDIA T4</option>
            <option value="nvidia-tesla-a100">NVIDIA A100 40G</option>
          </select>
        </div>

        <!-- GPU数量选择 -->
        <div v-if="formData.gpuType" class="form-group">
          <label for="gpuCount">{{ t('createVM.gpuCount') }}:</label>
          <select id="gpuCount" v-model.number="formData.gpuCount" @change="updateAvailableMachineTypes">
            <option value="1">1</option>
            <option value="2">2</option>
            <option value="4">4</option>
          </select>
        </div>

        <div class="form-group">
          <label for="vmMachineType">{{ t('createVM.machineType') }}:</label>
          <select id="vmMachineType" v-model="formData.machineType" required @change="updateCostEstimate" v-if="formData.gpuType !== 'nvidia-tesla-a100'">
              <option value="">{{ t('createVM.machineTypeRequired') }}</option>
              <optgroup v-if="!formData.gpuType" :label="t('createVM.machineTypeGroups.e2Series')">
              <option v-for="type in availableE2Types" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
              <optgroup v-if="!formData.gpuType" :label="t('createVM.machineTypeGroups.e2Standard')">
              <option v-for="type in availableE2StandardTypes" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
              <optgroup v-if="!formData.gpuType" :label="t('createVM.machineTypeGroups.e2HighCpu')">
              <option v-for="type in availableE2HighCpuTypes" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
              <optgroup v-if="!formData.gpuType" :label="t('createVM.machineTypeGroups.n1Standard')">
              <option v-for="type in availableN1StandardTypes" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
              <optgroup v-if="formData.gpuType === 'nvidia-l4'" :label="t('createVM.machineTypeGroups.g2StandardL4')">
              <option v-for="type in availableG2Types" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
              <optgroup v-if="formData.gpuType === 'nvidia-tesla-t4'" :label="t('createVM.machineTypeGroups.n1StandardT4')">
              <option v-for="type in availableN1WithT4Types" :key="type.value" :value="type.value">
                {{ type.label }} - {{ type.spec }}
              </option>
            </optgroup>
          </select>

          <!-- A100 GPU自动选择机器类型 -->
          <div v-if="formData.gpuType === 'nvidia-tesla-a100'" class="auto-selected-type">
            <p>{{ t('vm.resizeVM.autoSelectedType') }}: <strong>{{ autoSelectedA2Type?.label }} - {{ autoSelectedA2Type?.spec }}</strong></p>
          </div>
        </div>

        <!-- 服务账号选择 -->
        <div class="form-group">
          <label for="serviceAccount">{{ t('createVM.serviceAccount') }}:</label>
          <select id="serviceAccount" v-model="formData.serviceAccount">
            <option value="">{{ t('createVM.defaultServiceAccount') }}</option>
            <option v-for="sa in serviceAccounts" :key="sa" :value="sa">{{ sa }}</option>
          </select>
        </div>

        <!-- 成本估算 -->
        <CostEstimateDisplay
          :monthlyCost="monthlyCost"
          :gpuType="formData.gpuType"
          :gpuCount="formData.gpuCount"
          :diskSizeGb="0"
        />

        <div class="form-actions">
          <button type="submit" class="btn btn-success" :disabled="loading">
            {{ loading ? t('vm.resizeVM.resizing') : t('vm.resizeVM.resize') }}
          </button>
          <button type="button" class="btn btn-secondary" @click="$emit('close')">
            {{ t('vm.resizeVM.cancel') }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, nextTick, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useResources } from '../composables/useResources';
import { useCostEstimation } from '../composables/useCostEstimation';
import { useCloudProvider } from '../composables/useCloudProvider.js';
import CostEstimateDisplay from './CostEstimateDisplay.vue';

export default {
  name: 'ResizeVMModal',
  components: {
    CostEstimateDisplay
  },
  props: {
    vm: {
      type: Object,
      required: true
    }
  },
  emits: ['close', 'resized'],
  setup(props, { emit }) {
    const { t } = useI18n();
    const { currentProjectId } = useResources();
    const { monthlyCost, calculateCost } = useCostEstimation();
    const { currentProvider } = useCloudProvider();
    const loading = ref(false);

    // 创建vm的计算属性
    const vm = computed(() => props.vm);

    // 服务账号相关
    const serviceAccounts = ref([]);
    const serviceAccountsLoading = ref(false);

    const formData = reactive({
      machineType: '',
      gpuType: '',
      gpuCount: 1,
      serviceAccount: ''
    });

    // All VM type definitions (从CreateVMModal复制)
    const e2Types = [
      { value: 'e2-micro', label: 'e2-micro', spec: '0.5-2 vCPU, 1 GB RAM' },
      { value: 'e2-small', label: 'e2-small', spec: '0.5-2 vCPU, 2 GB RAM' },
      { value: 'e2-medium', label: 'e2-medium', spec: '1-2 vCPU, 4 GB RAM' }
    ];

    const e2StandardTypes = [
      { value: 'e2-standard-16', label: 'e2-standard-16', spec: '16 vCPU, 64 GB RAM' },
      { value: 'e2-standard-32', label: 'e2-standard-32', spec: '32 vCPU, 128 GB RAM' }
    ];

    const e2HighCpuTypes = [
      { value: 'e2-highcpu-16', label: 'e2-highcpu-16', spec: '16 vCPU, 16 GB RAM' },
      { value: 'e2-highcpu-32', label: 'e2-highcpu-32', spec: '32 vCPU, 32 GB RAM' }
    ];

    const n1StandardTypes = [
      { value: 'n1-standard-16', label: 'n1-standard-16', spec: '16 vCPU, 60 GB RAM' },
      { value: 'n1-standard-32', label: 'n1-standard-32', spec: '32 vCPU, 120 GB RAM' }
    ];

    // G2 series (L4 GPU)
    const g2StandardTypes = [
      { value: 'g2-standard-16', label: 'g2-standard-16', spec: '16 vCPU, 64 GB RAM' },
      { value: 'g2-standard-32', label: 'g2-standard-32', spec: '32 vCPU, 128 GB RAM' }
    ];

    // A2 series (A100 GPU)
    const a2HighGpuTypes = [
      { value: 'a2-highgpu-1g', label: 'a2-highgpu-1g', spec: '12 vCPU, 85 GB RAM' },
      { value: 'a2-highgpu-2g', label: 'a2-highgpu-2g', spec: '24 vCPU, 170 GB RAM' },
      { value: 'a2-highgpu-4g', label: 'a2-highgpu-4g', spec: '48 vCPU, 340 GB RAM' }
    ];

    // Available machine types (dynamically adjusted based on GPU selection)
    const availableE2Types = ref([...e2Types]);
    const availableE2StandardTypes = ref([...e2StandardTypes]);
    const availableE2HighCpuTypes = ref([...e2HighCpuTypes]);
    const availableN1StandardTypes = ref([...n1StandardTypes]);
    const availableG2Types = ref([]);
    const availableN1WithT4Types = ref([]);
    const availableA2Types = ref([]);

    // Auto-selected A2 machine type (based on GPU count)
    const autoSelectedA2Type = computed(() => {
      if (formData.gpuType === 'nvidia-tesla-a100' && formData.gpuCount) {
        const gpuCount = Number(formData.gpuCount);
        return a2HighGpuTypes.find(type => {
          const match = type.value.match(/-(\d+)g$/);
          return match && Number(match[1]) === gpuCount;
        }) || null;
      }
      return null;
    });

    // 获取当前VM的机器类型
    const getCurrentMachineType = () => {
      if (!vm.value.machineType) {return 'N/A';}
      const parts = vm.value.machineType.split('/');
      return parts[parts.length - 1];
    };

    // 获取当前GPU信息
    const getCurrentGPUInfo = () => {
      if (!vm.value.guestAccelerators?.length) {return 'N/A';}
      const gpu = vm.value.guestAccelerators[0];
      const gpuType = gpu.acceleratorType.split('/').pop();
      return `${gpu.acceleratorCount}x ${gpuType}`;
    };

    // 获取当前服务账号
    const getCurrentServiceAccount = () => {
      if (!vm.value.serviceAccounts?.length) {return t('createVM.defaultServiceAccount');}
      return vm.value.serviceAccounts[0].email || t('createVM.defaultServiceAccount');
    };

    // 加载服务账号
    const loadServiceAccounts = async () => {
      try {
        serviceAccountsLoading.value = true;
        const response = await fetch(`/api/service-accounts?projectId=${currentProjectId.value}`);
        const data = await response.json();
        serviceAccounts.value = data.accounts || [];
      } catch (error) {
        console.error('Failed to load service accounts:', error);
        serviceAccounts.value = [];
      } finally {
        serviceAccountsLoading.value = false;
      }
    };

    // 初始化表单数据
    const initializeFormData = () => {
      // 先设置GPU信息
      if (vm.value.guestAccelerators?.length) {
        const gpu = vm.value.guestAccelerators[0];
        formData.gpuType = gpu.acceleratorType.split('/').pop();
        formData.gpuCount = gpu.acceleratorCount;
      } else {
        formData.gpuType = '';
        formData.gpuCount = 1;
      }

      // 更新可用的机器类型
      updateAvailableMachineTypes();

      // 然后设置机器类型（在updateAvailableMachineTypes之后）
      const currentMachineType = getCurrentMachineType();
      formData.machineType = currentMachineType;

      // 设置当前服务账号
      formData.serviceAccount = vm.value.serviceAccounts?.[0]?.email || '';

      // 更新成本估算
      updateCostEstimate();
    };

    const updateAvailableMachineTypes = () => {
      // 只有在GPU类型改变时才清空机器类型
      if (!formData.gpuType) {
        availableE2Types.value = [...e2Types];
        availableE2StandardTypes.value = [...e2StandardTypes];
        availableE2HighCpuTypes.value = [...e2HighCpuTypes];
        availableN1StandardTypes.value = [...n1StandardTypes];
        availableG2Types.value = [];
        availableN1WithT4Types.value = [];
        availableA2Types.value = [];
      } else if (formData.gpuType === 'nvidia-l4') {
        availableE2Types.value = [];
        availableE2StandardTypes.value = [];
        availableE2HighCpuTypes.value = [];
        availableN1StandardTypes.value = [];
        availableG2Types.value = [...g2StandardTypes];
        availableN1WithT4Types.value = [];
        availableA2Types.value = [];
      } else if (formData.gpuType === 'nvidia-tesla-t4') {
        availableE2Types.value = [];
        availableE2StandardTypes.value = [];
        availableE2HighCpuTypes.value = [];
        availableN1StandardTypes.value = [...n1StandardTypes];
        availableG2Types.value = [];
        availableN1WithT4Types.value = [...n1StandardTypes];
        availableA2Types.value = [];
      } else if (formData.gpuType === 'nvidia-tesla-a100') {
        availableE2Types.value = [];
        availableE2StandardTypes.value = [];
        availableE2HighCpuTypes.value = [];
        availableN1StandardTypes.value = [];
        availableG2Types.value = [];
        availableN1WithT4Types.value = [];
        availableA2Types.value = [];
        if (autoSelectedA2Type.value) {
          formData.machineType = autoSelectedA2Type.value.value;
          nextTick(() => {
            updateCostEstimate();
          });
        }
      }
      updateCostEstimate();
    };

    const updateCostEstimate = () => {
      calculateCost(formData.machineType, formData.gpuType, formData.gpuCount, 0, currentProvider.value);
    };

    const handleSubmit = async () => {
      loading.value = true;
      try {
        const response = await fetch('/api/vm/resize', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            projectId: currentProjectId.value,
            zone: vm.value.zone,
            instanceName: vm.value.name,
            machineType: formData.machineType,
            gpuType: formData.gpuType || null,
            gpuCount: formData.gpuCount || null,
            serviceAccount: formData.serviceAccount || null
          })
        });

        if (response.ok) {
          emit('resized');
        } else {
          const error = await response.json();
          throw new Error(error.error || t('vm.resizeVM.error.resizeFailed'));
        }
      } catch (error) {
        console.error('VM resize failed:', error);
        alert(error.message);
      } finally {
        loading.value = false;
      }
    };

    // Listen for changes in GPU type and count
    watch([
      () => formData.gpuType,
      () => formData.gpuCount
    ], () => {
      if (formData.gpuType === 'nvidia-tesla-a100' && autoSelectedA2Type.value) {
        formData.machineType = autoSelectedA2Type.value.value;
        nextTick(() => {
          updateCostEstimate();
        });
      } else if (formData.gpuType === 'nvidia-tesla-a100' && !autoSelectedA2Type.value) {
        formData.machineType = '';
        updateCostEstimate();
      } else {
        updateCostEstimate();
      }
    });

    onMounted(() => {
      loadServiceAccounts();
      initializeFormData();
    });

    return {
      t,
      loading,
      formData,
      vm,
      availableE2Types,
      availableE2StandardTypes,
      availableE2HighCpuTypes,
      availableN1StandardTypes,
      availableG2Types,
      availableN1WithT4Types,
      availableA2Types,
      autoSelectedA2Type,
      monthlyCost,
      serviceAccounts,
      serviceAccountsLoading,
      getCurrentMachineType,
      getCurrentGPUInfo,
      getCurrentServiceAccount,
      updateAvailableMachineTypes,
      updateCostEstimate,
      handleSubmit
    };
  }
};
</script>

<style scoped>
.modal {
  position: fixed;
  z-index: 1000;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-content {
  background-color: white;
  padding: 2rem;
  border-radius: 8px;
  max-width: 600px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.close {
  position: absolute;
  right: 1rem;
  top: 1rem;
  font-size: 1.5rem;
  cursor: pointer;
  color: #666;
}

.close:hover {
  color: #000;
}

.current-specs {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  margin-bottom: 1.5rem;
}

.current-specs h3 {
  margin-top: 0;
  margin-bottom: 1rem;
  color: #333;
}

.spec-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
}

.spec-label {
  font-weight: 500;
  color: #666;
}

.spec-value {
  font-weight: 600;
  color: #333;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #333;
}

.form-group select {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
}

.auto-selected-type {
  background-color: #e3f2fd;
  padding: 1rem;
  border-radius: 4px;
  margin-top: 0.5rem;
}


.form-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
}

.btn {
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 1rem;
  transition: background-color 0.2s;
}

.btn-success {
  background-color: #28a745;
  color: white;
}

.btn-success:hover:not(:disabled) {
  background-color: #218838;
}

.btn-success:disabled {
  background-color: #6c757d;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #545b62;
}
</style>
